import React, {
	FormEventHandler,
	forwardRef,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'
import dynamic from 'next/dynamic'

import PaystubTemplate1 from 'components/blocks/documents/DocumentPaystub/templates/PaystubTemplate1'
import Button from 'components/elements/Button'
import MobxTextInput from 'components/elements/fields/MobxTextInput'
import SpinnerDots from 'components/elements/SpinnerDots'
import paystubDefaults from 'src/components/blocks/documents/DocumentPaystub/templates/config/templates.defaultValues'
import {proxyDefaultWrapper} from 'src/utils'
import {usePayStubStore} from 'src/utils/contexts/RootStore.context'

import s from './PayStubChooseTemplate.module.scss'
import {TemplatePagination} from './TemplatePagination'
import {TemplateThemes} from './TemplateThemes'

const cx = cn.bind(s)

const Loading = () => (
	<div className={cx('loading')}>
		<SpinnerDots size="m" position="center" />
	</div>
)

const PaystubTemplate2 = dynamic(
	() => import('components/blocks/documents/DocumentPaystub/templates/PaystubTemplate2'),
	{
		loading: Loading,
	},
)
const PaystubTemplate3 = dynamic(
	() => import('components/blocks/documents/DocumentPaystub/templates/PaystubTemplate3'),
	{
		loading: Loading,
	},
)
const PaystubTemplate4 = dynamic(
	() => import('components/blocks/documents/DocumentPaystub/templates/PaystubTemplate4'),
	{
		loading: Loading,
	},
)
const PaystubTemplate5 = dynamic(
	() => import('components/blocks/documents/DocumentPaystub/templates/PaystubTemplate5'),
	{
		loading: Loading,
	},
)
const PaystubTemplate6 = dynamic(
	() => import('components/blocks/documents/DocumentPaystub/templates/PaystubTemplate6'),
	{
		loading: Loading,
	},
)

type TDefaultValues = {
	coNumber: string
	fileNumber: string
	deptNumber: string
	clockNumber: string
	vchrNumber: string
}

interface IPayStubChooseTemplateProps {
	className?: string
	children?: ReactNode | string
	templates: IPayStubTemplates[]
	activeTemplate: IPayStubTemplates | undefined
	handleTemplateTheme: (id: number, theme: IPayStubTemplates['theme']) => void
	handleActiveTemplate: (id: number) => void
	onChange?: FormEventHandler<HTMLDivElement>
}

const PayStubChooseTemplate = forwardRef<HTMLDivElement, IPayStubChooseTemplateProps>(
	(
		{
			className,
			children,
			templates,
			activeTemplate,
			handleTemplateTheme,
			handleActiveTemplate,
			...props
		},
		ref,
	) => {
		const [isMounted, setMounted] = useState(false)
		const payStubStore = usePayStubStore()
		const payStubStoreWithDefaults = proxyDefaultWrapper(payStubStore, paystubDefaults)
		const {
			payStubsNumber,
			coNumber,
			fileNumber,
			deptNumber,
			clockNumber,
			vchrNumber,
			isCalculationLoading,
			isCalculationValid,
		} = payStubStore
		const [defaultValues] = useState<TDefaultValues>({
			coNumber,
			fileNumber,
			deptNumber,
			clockNumber,
			vchrNumber,
		})

		useEffect(() => setMounted(true), [])

		const handleFocusEvent = useCallback(
			(e: React.FocusEvent<HTMLInputElement>) => {
				const name = e.target.name
				if (e.target.value === '') {
					payStubStore[name as keyof TDefaultValues] = defaultValues[name as keyof TDefaultValues]
				}
			},
			[defaultValues, payStubStore],
		)

		const PaystubTemplate = useMemo(() => {
			switch (activeTemplate?.id) {
				case 2:
					return PaystubTemplate2
				case 3:
					return PaystubTemplate3
				case 4:
					return PaystubTemplate4
				case 5:
					return PaystubTemplate5
				case 6:
					return PaystubTemplate6

				default:
					return PaystubTemplate1
			}
		}, [activeTemplate?.id])

		const payStubs = Number(payStubsNumber)

		return (
			<div className={cx('template', className)} ref={ref} {...props}>
				{children}
				<ul className={cx('template--list', 'list')}>
					{templates.map(({id, isActive}) => (
						<li key={id} className={cx('list__item')} data-testid="list-item">
							<Button
								className={cx('list__btn', isActive ? 'active' : '')}
								onClick={() => handleActiveTemplate(id)}
								type="button"
								size="small"
								variant="secondary">
								{id}
							</Button>
						</li>
					))}
				</ul>

				<div className={cx('template--fields')}>
					{activeTemplate?.id === 1 && (
						<>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={
									<>
										CO.
										<br className="d-md-none" />
										number
									</>
								}
								placeholder="yxx"
								store={payStubStore}
								name="coNumber"
								type="text"
								maxLength={3}
								onBlur={handleFocusEvent}
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={
									<>
										File <br className="d-md-none" />
										number
									</>
								}
								placeholder="630690"
								store={payStubStore}
								name="fileNumber"
								isNumber
								maxLength={6}
								onBlur={handleFocusEvent}
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={
									<>
										DEPT. <br className="d-md-none" />
										number
									</>
								}
								placeholder="015792"
								store={payStubStore}
								name="deptNumber"
								isNumber
								maxLength={6}
								onBlur={handleFocusEvent}
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={
									<>
										Clock <br className="d-md-none" />
										number
									</>
								}
								placeholder="95035"
								store={payStubStore}
								name="clockNumber"
								isNumber
								maxLength={5}
								onBlur={handleFocusEvent}
							/>
							<MobxTextInput
								wrapperClassName={cx('field')}
								label={
									<>
										VCHR <br className="d-md-none" />
										Number
									</>
								}
								placeholder="0000007104"
								store={payStubStore}
								name="vchrNumber"
								isNumber
								maxLength={10}
								onBlur={handleFocusEvent}
							/>
						</>
					)}

					{activeTemplate?.id === 2 && (
						<MobxTextInput
							wrapperClassName={cx('field')}
							label={'Advice number'}
							placeholder="0000012837"
							store={payStubStore}
							name="vchrNumber"
							type="number"
							maxLength={10}
							onBlur={handleFocusEvent}
						/>
					)}
				</div>

				{activeTemplate?.theme && (
					<TemplateThemes
						activeTemplate={activeTemplate}
						handleTemplateTheme={handleTemplateTheme}
					/>
				)}

				{payStubs > 1 ? (
					<TemplatePagination
						payStubs={payStubs}
						setActiveDataTemplate={payStubStore.setActiveDataTemplate}
						activeDataTemplate={payStubStore.activeDataTemplate}
					/>
				) : null}
				<div className={cx('template', 'template--preview', 'scroll--preview')}>
					{isCalculationLoading && (
						<div className={cx('spinner')} data-testid="calculationLoading">
							<div className={cx('spinner__wrapper')}>
								<SpinnerDots className={cx('spinner__inner')} />
							</div>
						</div>
					)}
					{isMounted && (
						<PaystubTemplate
							className={cx('preview', {blur: isCalculationLoading || !isCalculationValid})}
							data={payStubStoreWithDefaults}
							hasFrame={true}
							activeItem={payStubStore.activeDataTemplate}
							theme={activeTemplate?.theme}
						/>
					)}
				</div>
			</div>
		)
	},
)

PayStubChooseTemplate.displayName = 'PayStubChooseTemplate'

export default observer(PayStubChooseTemplate)
